import Matter from "matter-js";
import type { Container } from "pixi.js";
import { Particle } from "./particles";
import type { ParticleType } from "./types";

export const GHOST_SPAWN_INTERVAL_MS = 700;
export const GHOST_MAX_PER_HOPPER = 4;
export const GHOST_SETTLE_THRESHOLD = 0.2;
export const GHOST_SETTLE_MS = 1000;

export interface GhostSpawner {
  x: number;
  y: number;
  type: ParticleType;
}

export interface GhostState {
  particles: Particle[];
  timerPerHopper: number[];
  settledAtPerParticle: Map<Particle, number>;
}

export function createGhostState(spawnerCount: number): GhostState {
  return {
    particles: [],
    timerPerHopper: new Array(spawnerCount).fill(0),
    settledAtPerParticle: new Map(),
  };
}

function spawnGhost(
  spawner: GhostSpawner,
  phys: { world: Matter.World },
  layer: Container,
  state: GhostState,
  particleR: number,
): void {
  const p = new Particle(spawner.type, spawner.x, spawner.y, particleR, true);
  Matter.World.add(phys.world, p.body);
  layer.addChild(p.view);
  state.particles.push(p);
}

export function tickGhosts(
  dt: number,
  state: GhostState,
  spawners: GhostSpawner[],
  phys: { world: Matter.World },
  layer: Container,
  now: number,
  workbenchBottomY: number,
  particleR = 11,
): void {
  for (let i = 0; i < spawners.length; i++) {
    state.timerPerHopper[i] = (state.timerPerHopper[i] ?? 0) + dt;
    const count = state.particles.filter((p) => p.type === spawners[i].type).length;
    if (state.timerPerHopper[i] >= GHOST_SPAWN_INTERVAL_MS && count < GHOST_MAX_PER_HOPPER) {
      state.timerPerHopper[i] = 0;
      spawnGhost(spawners[i], phys, layer, state, particleR);
    }
  }

  for (const p of [...state.particles]) {
    p.sync();
    const v = p.body.velocity;
    const speed = Math.hypot(v.x, v.y);
    if (speed < GHOST_SETTLE_THRESHOLD) {
      const since = state.settledAtPerParticle.get(p) ?? now;
      state.settledAtPerParticle.set(p, since);
      if (now - since > GHOST_SETTLE_MS) {
        removeGhost(p, phys, layer, state);
        continue;
      }
    } else {
      state.settledAtPerParticle.delete(p);
    }
    if (p.body.position.y > workbenchBottomY + 80) {
      removeGhost(p, phys, layer, state);
    }
  }
}

function removeGhost(
  p: Particle,
  phys: { world: Matter.World },
  layer: Container,
  state: GhostState,
): void {
  Matter.World.remove(phys.world, p.body);
  layer.removeChild(p.view);
  state.particles = state.particles.filter((x) => x !== p);
  state.settledAtPerParticle.delete(p);
}

export function clearGhosts(
  state: GhostState,
  phys: { world: Matter.World },
  layer: Container,
): void {
  for (const p of state.particles) {
    Matter.World.remove(phys.world, p.body);
    layer.removeChild(p.view);
  }
  state.particles = [];
  state.settledAtPerParticle.clear();
  for (let i = 0; i < state.timerPerHopper.length; i++) state.timerPerHopper[i] = 0;
}
